import { useCallback, useMemo, useState } from 'react';

const DEFAULT_DRAWER_HEIGHT = 'half';
const DEFAULT_MODE = 'auto';

export const DEFAULT_AI_ISLAND_STATE = {
  isExpanded: false,
  drawerHeight: DEFAULT_DRAWER_HEIGHT,
  mode: DEFAULT_MODE,
  attachedSelection: null,
  promptValue: '',
  isStreaming: false,
  aiResponse: null,
  pendingAction: null,
};

export function useAIIslandState(options = {}) {
  const { initialExpanded = false, expanded, onExpandedChange } = options;
  const [internalExpanded, setInternalExpanded] = useState(initialExpanded);
  const [drawerHeight, setDrawerHeight] = useState(DEFAULT_DRAWER_HEIGHT);
  const [mode, setMode] = useState(DEFAULT_MODE);
  const [attachedSelection, setAttachedSelection] = useState(null);
  const [promptValue, setPromptValue] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);

  const isExpanded = expanded ?? internalExpanded;

  const setExpanded = useCallback(
    (nextValue) => {
      const resolvedValue = typeof nextValue === 'function' ? nextValue(isExpanded) : nextValue;
      if (expanded == null) {
        setInternalExpanded(resolvedValue);
      }
      if (typeof onExpandedChange === 'function') {
        onExpandedChange(resolvedValue);
      }
    },
    [expanded, isExpanded, onExpandedChange]
  );

  const resetResponse = useCallback(() => {
    setAiResponse(null);
    setPendingAction(null);
  }, []);

  return useMemo(
    () => ({
      isExpanded,
      setExpanded,
      drawerHeight,
      setDrawerHeight,
      mode,
      setMode,
      attachedSelection,
      setAttachedSelection,
      promptValue,
      setPromptValue,
      isStreaming,
      setIsStreaming,
      aiResponse,
      setAiResponse,
      pendingAction,
      setPendingAction,
      resetResponse,
    }),
    [
      isExpanded,
      setExpanded,
      drawerHeight,
      mode,
      attachedSelection,
      promptValue,
      isStreaming,
      aiResponse,
      pendingAction,
      resetResponse,
    ]
  );
}
